import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "../api/registration.api";

export const REGISTRATION_QUERY_KEYS = {
	list: (params: any) => ["employees", "list", params],
	detail: (id: string | number) => ["employee", "detail", id],
};

export const useEmployees = (
	params: {
		page?: number;
		limit?: number;
		search?: string;
		type_id?: string | number;
		district_id?: string | number;
		status?: string;
	} = {},
) => {
	const query = useQuery({
		queryKey: REGISTRATION_QUERY_KEYS.list(params),
		queryFn: () => api.fetchEmployeeList(params),
		staleTime: 5 * 60 * 1000, // 5 minutes
		refetchOnWindowFocus: false,
	});

	return {
		...query,
		employees: query.data?.items ?? [],
		totalCount: query.data?.totalItems ?? 0,
		totalPages: query.data?.totalPages ?? 1,
	};
};

export const useEmployeeById = (id: string | number | undefined) => {
	const query = useQuery({
		queryKey: REGISTRATION_QUERY_KEYS.detail(id!),
		queryFn: () => api.fetchEmployeeById(id!),
		enabled: !!id,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});

	return {
		...query,
		employee: query.data?.data?.employee ?? null,
	};
};

export const useDeleteEmployee = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: api.deleteEmployee,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["employees", "list"] });
		},
	});
};

/* --------- EMPLOYEE REGISTRATION MULTI-STEP MUTATIONS --------- */

export const useSavePersonalDetails = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ payload, id }: { payload: any; id?: string | number }) =>
			api.savePersonalDetails(payload, id),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["employees", "list"] });
			if (variables.id) {
				queryClient.invalidateQueries({
					queryKey: REGISTRATION_QUERY_KEYS.detail(variables.id),
				});
			}
		},
	});
};

export const useSaveFamilyDetails = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, payload }: { id: string | number; payload: any }) =>
			api.saveFamilyDetails(id, payload),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["employees", "list"] });
			queryClient.invalidateQueries({
				queryKey: REGISTRATION_QUERY_KEYS.detail(variables.id),
			});
		},
	});
};

export const useSaveEducationExperience = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, payload }: { id: string | number; payload: any }) =>
			api.saveEducationExperience(id, payload),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["employees", "list"] });
			queryClient.invalidateQueries({
				queryKey: REGISTRATION_QUERY_KEYS.detail(variables.id),
			});
		},
	});
};

export const useSaveWorkingDetails = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, payload }: { id: string | number; payload: any }) =>
			api.saveWorkingDetails(id, payload),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["employees", "list"] });
			queryClient.invalidateQueries({
				queryKey: REGISTRATION_QUERY_KEYS.detail(variables.id),
			});
		},
	});
};

export const useSaveTrainingAuditDetails = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, payload }: { id: string | number; payload: any }) =>
			api.saveTrainingAuditDetails(id, payload),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["employees", "list"] });
			queryClient.invalidateQueries({
				queryKey: REGISTRATION_QUERY_KEYS.detail(variables.id),
			});
		},
	});
};

/* --------- COMPLAINT HOOKS --------- */

export const useComplaints = (id: string | number | undefined) => {
	const query = useQuery({
		queryKey: ["complaints", id],
		queryFn: () => api.fetchComplaints(id!),
		enabled: !!id,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});

	return {
		...query,
		complaints: query.data ?? [],
	};
};

export const useSaveComplaint = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id, payload }: { id: string | number; payload: any }) =>
			api.saveComplaint(id, payload),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["complaints", variables.id] });
		},
	});
};
